import { CLAMAV_SCAN_KEY, ZOHO_BOOKS_SOURCE_KEY, type IntegrationSeam } from './integrations';
import { ANTHROPIC_PROVIDER } from '../ai/aiSeam';
import { PEOPLE_DATA_VENDOR } from '../ai/enrichmentSeam';
import { PLAID_FEED_KEY, STRIPE_FEED_KEY } from './bankFeeds';
import { STRIPE_GATEWAY_KEY } from './payments';
import type { ScannerHealth } from '../files/scannerHealth';

/**
 * 🔒 Settings › System › Server info › Status (decision 1870; T1230): what
 * answers each seam the API has, and whether it answered when asked. The Status
 * tab and each Settings › Integrations card read the same three answers through
 * `seamReading`, so the two screens never disagree.
 *
 * 🔨 Nothing here is stored. What answers a seam is read from the bindings and
 * the saved rows on every call, and a live check is asked on request, never
 * kept, so no column holds a last-checked time.
 */

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const SYSTEM_STATUS_PATH = '/system-status';

/** One seam's own check: each is its own request, so a slow service holds only its own row. */
export function systemStatusCheckPath(seam: StatusSeam): string {
  return `${SYSTEM_STATUS_PATH}/${seam}/check`;
}

/** Every seam the Status tab lists, in its order: the two the application stands on, then the services a person meets. */
export const STATUS_SEAMS = [
  'database', 'objectStorage', 'malwareScan', 'outgoingMail', 'mailbox', 'textMessages', 'phoneCalls', 'payments', 'bankFeed',
  'maps', 'addressCheck', 'booksSource', 'signIn', 'eSignature', 'pdfRenderer', 'payroll', 'ai', 'enrichmentPeopleData', 'enrichmentWebsite', 'enrichmentSearch', 'keyStore',
] as const;
export type StatusSeam = (typeof STATUS_SEAMS)[number];

/** The seams the server can ask a live check of; every other vendor-backed seam answers notChecked (T1237). */
export const CHECKED_SEAMS: readonly StatusSeam[] = ['database', 'objectStorage', 'malwareScan', 'payments'];

/** 🔒 The seams whose stand-in matters outside a test box (decision 1870): a stand-in on one of them draws the warning tone. */
export const STAND_IN_WARNED: readonly StatusSeam[] = ['malwareScan', 'outgoingMail', 'payments', 'signIn'];

export function isStatusSeam(value: unknown): value is StatusSeam {
  return typeof value === 'string' && (STATUS_SEAMS as readonly string[]).includes(value);
}

/**
 * What answers a seam now. A vendor carries its key and, where it has one, the
 * host and port it is reached at, never a credential or a path. The stand-in is
 * the built-in fake that answers everything and does nothing outside; off is a
 * seam nobody answers, by choice.
 */
export type SeamAnswering = {
  kind: 'vendor';
  vendor: string;
  address: string | null;
  /** 🔒 Decision 1950: the address came from the deployment's environment, not a Settings row; absent otherwise. */
  fromEnvironment?: boolean;
} | {
  kind: 'standIn';
  /** 🔒 Decision 3760: the Key store's stand-in keyed from the test environment's fixed seed, whose sealed values open again after a restart; absent otherwise. */
  keptAcrossRestarts?: true;
} | { kind: 'off' };

/** Why a seam was not checked: nothing outside answers it, it is off, or its vendor offers no cheap check. */
export type SeamUncheckedReason = 'standIn' | 'off' | 'noCheck';

/** A live check's answer, with when it was asked; the scanner's answer carries its health (decision 1952). */
export type SeamCheck = { kind: 'answered'; at: string; scanner?: ScannerHealth } | { kind: 'notAnswered'; at: string; reason: string | null } | { kind: 'notChecked'; why: SeamUncheckedReason };

/** One row of the Status tab's read: the seam, what answers it, and whether a check can be asked of it. */
export interface SeamStatusRow {
  seam: StatusSeam;
  answering: SeamAnswering;
  checkable: boolean;
}

export interface SystemStatusAnswer {
  readAt: string;
  rows: SeamStatusRow[];
}

export interface SeamCheckAnswer {
  seam: StatusSeam;
  check: SeamCheck;
}

export type SeamTone = 'plain' | 'warning' | 'danger';

/** 🔒 The one reading both screens draw (decision 1870): what answers, what the check said, and the tone. */
export interface SeamReading {
  answering: SeamAnswering['kind'];
  /** `checking` while the check's own request is out. */
  check: SeamCheck['kind'] | 'checking';
  tone: SeamTone;
}

/**
 * 🔒 Not answering draws the danger tone; a stand-in on a seam that matters
 * outside a test box draws the warning tone, and so does a scanner answering
 * with signatures past the days allowed (decision 1952); everything else is
 * plain.
 */
export function seamReading(seam: StatusSeam, answering: SeamAnswering, check: SeamCheck | null): SeamReading {
  const checkKind = check?.kind ?? 'checking';
  const staleSignatures = check?.kind === 'answered' && check.scanner?.signaturesStale === true;
  const tone: SeamTone = checkKind === 'notAnswered' ? 'danger' : (answering.kind === 'standIn' && STAND_IN_WARNED.includes(seam)) || staleSignatures ? 'warning' : 'plain';
  return { answering: answering.kind, check: checkKind, tone };
}

/** The check a seam with nothing outside to ask reads, without a request. */
export function uncheckedFor(answering: SeamAnswering): SeamCheck | null {
  if (answering.kind === 'standIn') return { kind: 'notChecked', why: 'standIn' };
  if (answering.kind === 'off') return { kind: 'notChecked', why: 'off' };
  return null;
}

/** How long ago a check was asked, in the largest whole unit: under a minute reads as now. */
export function checkedAgo(at: string, now: Date): { unit: 'now' | 'minutes' | 'hours' | 'days'; count: number } {
  const ms = now.getTime() - new Date(at).getTime();
  if (!Number.isFinite(ms) || ms < 60_000) return { unit: 'now', count: 0 };
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return { unit: 'minutes', count: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { unit: 'hours', count: hours };
  return { unit: 'days', count: Math.floor(hours / 24) };
}

/** The facts an Integrations seam's answer needs beyond the card's own active provider. */
export interface IntegrationSeamFacts {
  /** The scanner's host and port in force, held or from the environment, or null where neither names one. */
  scannerAddress: string | null;
  /** 🔒 Decision 1950: true where the address is the environment's because no row is held. */
  scannerFromEnvironment?: boolean;
  /** Whether sign-in is enforced; in sandbox mode nobody signs in. */
  enforced: boolean;
}

const vendor = (key: string, address: string | null = null): SeamAnswering => ({ kind: 'vendor', vendor: key, address });
const STAND_IN: SeamAnswering = { kind: 'standIn' };
const OFF: SeamAnswering = { kind: 'off' };

/** The payment gateway and bank feed keys of the built-in fakes. */
export const FAKE_GATEWAY_KEY = 'fake';
export const SCRIPTED_FEED_KEY = 'scripted';

/**
 * 🔒 What answers one of the six seams an Integrations card switches, from the
 * provider the card names as active (decision 1870). The Status tab reads its
 * six rows through this too, so a card and its row say the same thing.
 */
export function integrationSeamAnswering(seam: IntegrationSeam, active: string | null, facts: IntegrationSeamFacts): SeamAnswering {
  switch (seam) {
    case 'malwareScan':
      if (active !== CLAMAV_SCAN_KEY) return STAND_IN;
      return facts.scannerFromEnvironment ? { kind: 'vendor', vendor: CLAMAV_SCAN_KEY, address: facts.scannerAddress, fromEnvironment: true } : vendor(CLAMAV_SCAN_KEY, facts.scannerAddress);
    case 'signIn':
      // Single sign-on off leaves the built-in password sign-in, which is real; sandbox mode signs nobody in.
      return active ? vendor(active) : facts.enforced ? vendor('passwords') : STAND_IN;
    case 'payments':
      return active === null ? OFF : active === FAKE_GATEWAY_KEY ? STAND_IN : vendor(active);
    case 'bankFeed':
      return active === null ? OFF : active === SCRIPTED_FEED_KEY ? STAND_IN : vendor(active);
    case 'maps':
      return active === null || active === 'Off' ? OFF : vendor(active);
    case 'booksSource':
      return active === ZOHO_BOOKS_SOURCE_KEY ? vendor(ZOHO_BOOKS_SOURCE_KEY) : OFF;
    case 'ai':
      // 🔒 Decision 5380: the seam binds Anthropic only with the switch on, a key and a model held; anything less sends nothing.
      return active === ANTHROPIC_PROVIDER ? vendor(ANTHROPIC_PROVIDER) : OFF;
    case 'enrichmentPeopleData':
      // 🔒 Decision 5381: the people-data adapter binds with its line on, its key and its price held.
      return active === PEOPLE_DATA_VENDOR ? vendor(PEOPLE_DATA_VENDOR) : OFF;
    case 'enrichmentWebsite':
    case 'enrichmentSearch':
      // 🔒 Decision 5381: the website and search adapters read through the AI seam, so Anthropic answers them or nothing does.
      return active === ANTHROPIC_PROVIDER ? vendor(ANTHROPIC_PROVIDER) : OFF;
  }
}

/** The vendors a word list names; a key outside it (a sign-in provider's own name) reads as itself. */
export const SEAM_VENDOR_KEYS = [
  'postgres', 's3', CLAMAV_SCAN_KEY, STRIPE_GATEWAY_KEY, PLAID_FEED_KEY, STRIPE_FEED_KEY, 'GoogleMaps', 'OpenStreetMap', ZOHO_BOOKS_SOURCE_KEY,
  'imap', 'chromium', 'hostKey', 'passwords', ANTHROPIC_PROVIDER, PEOPLE_DATA_VENDOR,
] as const;
